import { useState } from "react";
import Footer from "../components/common/footer";
import ButtonSubmit from "../components/common/buttonSubmit";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../assets/images/logoFederfarma.png";
import { useForm } from "react-hook-form";
import InputFiledRHF from "../components/common/RHF/inputFiledRHF";
import i18n from "../i18n/config";
import { updatePassword } from "../redux/reducer/changePasswordReducer";
import { useAppDispatch } from "../redux";
import { CheckNavigation, logOut } from "../services/Auth";
import { initLoginData, updateUser } from "../redux/reducer/LoginReducer";
import SuccessAlertMessage from "../components/alertMessageModal/SuccessAlertMessage";
import ErrorAlertMessage from "../components/alertMessageModal/ErrorAlertMessage";
import { useSelector } from "react-redux";
import { getErrorMessage } from "../utils/helpers";

const ChangePassword = () => {
  const lang = i18n.language;
  const [spinner, setSpinner] = useState(false);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [notificationType, setNotificationType] = useState("");
  const [success, setSuccess] = useState(false);

  const { user, saml } = useSelector((store: any) => store.login);
  const { changePassword } = useSelector((state: any) => state);
  const { apiError } = changePassword;

  const passwordRules = [
    t("password-rules.password_rule_length"),
    t("password-rules.password_rule_uppercase"),
    t("password-rules.password_rule_lowercase"),
    t("password-rules.password_rule_number"),
    t("password-rules.password_rule_special_char"),
    t("password-rules.password_rule_different_last_5"),
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const newPassword = watch("newPassword");

    /**
   * Update some state and redirect to the right page.
   * @param checkNavigationResponse /checknavigation response with entity IDs and navigation detail
   */
    const updateStateAndNavigate = (checkNavigationResponse: any) => {
      dispatch(initLoginData(checkNavigationResponse));
      navigate(checkNavigationResponse.navigation);
    };

  const handleUpdate = async (data: any) => {
    setSpinner(true);
    try {
      const { currentPassword, newPassword } = data;
      const result = await dispatch(
        updatePassword(currentPassword, newPassword)
      );
      if (result) {
        dispatch(updateUser({ ...user, forcePasswordChange: false }));
        setSuccess(true);
      } else {
        setSuccess(false);
        setNotificationType("error");
      }
    } catch (error) {
      setSuccess(false);
      setNotificationType("error");
      console.error("update password error:", error);
    } finally {
      setSpinner(false);
    }
  };
  /**
   * update notificationType value
   */
  const handleClose = () => {
    setNotificationType("");
  };

  const handleLoginRedirect = async () => {
    await logOut();
    navigate("/login");
  };

  const handleRedirect = () => {
    CheckNavigation().then(updateStateAndNavigate);
  }

  const successComponent = () => (
    <div className="container min-vh-100 d-flex flex-column">
    <div className="text-center pt-4">
      <img src={logo} className="mb-4" alt="Logo" />
    </div>
    <div className="flex-grow-1 d-flex align-items-center justify-content-center">
      <div className="col-md-12 col-lg-4">
        <div className="TitleModal text-center mb-4">
          <h5>{t("change_your_password")}</h5>
        </div>
        <div className="mb-2 text-center">
          <label className="ms-2 label-md">
          {t("password-change-success")}
          </label>
        </div>
        <div className="text-center">
          <ButtonSubmit
            spinner={spinner}
            textButton={t("continue-navigation")}
            handleChange={handleRedirect}
            className="w-100"
          />
        </div>
        <Footer />
      </div>
    </div>  
  </div>
  )

  return (
    <>
      {notificationType === "error" && (
        <ErrorAlertMessage
          icon="info"
          handleClose={handleClose}
          message={
            apiError?.key
              ? getErrorMessage(apiError)
              : t("password-change-error")
          }
        />
      )}{
        success ? successComponent() 
        : <div className="container">
        <div className="TableCenter h-100 ">
          <div className="Center">
            <div className="col-md-12 col-lg-4 pl-0 pr-0 mx-auto pt-4">
              <div className="text-center">
                <img src={logo} className="mb-4 " alt="Logo" />
              </div>
              <div className="TitleModal text-center">
                <h5>{t("change_your_password")}</h5>
              </div>
              <div className="mb-2">
                <label className="ms-2 label-md">
                  {t("first_login_message")}
                </label>
              </div>

              <div className="mb-2">
                <label className="ms-2 label-md">
                  {t("secure_password_title")}
                </label>
                {passwordRules.map((rule, index) => (
                  <div key={index}>
                    <label className="ms-2 label-md">- {rule}</label>
                  </div>
                ))}
              </div>

              <div className="form-outline mb-2">
                <label className="pb-1 ms-3">{t("current_password")}</label>
                <InputFiledRHF
                  formField={{
                    translations: [{ iso: lang, value: t("current_password") }],
                    name: "currentPassword",
                    type: "password",
                  }}
                  register={{
                    ...register("currentPassword", {
                      required: t("current_password_required"),
                      minLength: {
                        value: 8,
                        message: t("password_min_length"),
                      },
                      maxLength: {
                        value: 18,
                        message: t("password_max_length")
                      },
                    }),
                  }}
                  errors={errors}
                  className="input-bg-white"
                />
              </div>

              <div className="form-outline mb-2">
                <label className="pb-1 ms-3">{t("new_password")}</label>
                <InputFiledRHF
                  formField={{
                    translations: [{ iso: lang, value: t("new_password") }],
                    name: "newPassword",
                    type: "password",
                  }}
                  register={{
                    ...register("newPassword", {
                      required: t("new_password_required"),
                      minLength: {
                        value: 8,
                        message: t("password_min_length"),
                      },
                      maxLength: {
                        value: 18,
                        message: t("password_max_length")
                      },
                      validate: {
                        uppercase: (value) =>
                          /[A-Z]/.test(value) ||
                          t("password-rules.password_rule_uppercase"),
                        lowercase: (value) =>
                          /[a-z]/.test(value) ||
                          t("password-rules.password_rule_lowercase"),
                        number: (value) =>
                          /[0-9]/.test(value) ||
                          t("password-rules.password_rule_number"),
                        specialChar: (value) =>
                          /[^A-Za-z0-9]/.test(value) ||
                          t("password-rules.password_rule_special_char"),
                      },
                    }),
                  }}
                  errors={errors}
                  className="input-bg-white"
                />
              </div>

              <div className="form-outline mb-4">
                <label className="pb-1 ms-3">{t("confirm_password")}</label>
                <InputFiledRHF
                  formField={{
                    translations: [{ iso: lang, value: t("confirm_password") }],
                    name: "confirmPassword",
                    type: "password",
                  }}
                  register={{
                    ...register("confirmPassword", {
                      required: t("confirm_password_required"),
                      minLength: {
                        value: 8,
                        message: t("password_min_length"),
                      },
                      validate: (value) =>
                        value === newPassword || t("password_not_match"),
                    }),
                  }}
                  errors={errors}
                  className="input-bg-white"
                />
              </div>
              <div className="text-center">
                <ButtonSubmit
                  spinner={spinner}
                  textButton={t("change_password")}
                  handleChange={handleSubmit(handleUpdate)}
                  className="w-100"
                />
              </div>

              <div className="text-link mt-2" onClick={handleLoginRedirect}>
                {t("back_to_login")}
              </div>
              <Footer />
            </div>
          </div>
        </div>
      </div>
      }
      
    </>
  );
};

export default ChangePassword;
